import React from "react";
import { useI18n } from "../i18n";
import { money as fmtMoney } from "../lib/format";

export const SCOPE_COLOR = { personal: "#35BBAE", professional: "#075E62", total: "#8A9592" };

export const btn = {
  primary: "inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full font-medium text-sm transition-colors duration-150 bg-[#075E62] text-[#F7FBFA] hover:bg-[#35BBAE] hover:text-[#075E62] focus-visible:ring-2 focus-visible:ring-[#35BBAE] focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed",
  accent: "inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full font-semibold text-sm transition-colors duration-150 bg-[#05787E] text-white hover:bg-[#075E62] hover:text-white focus-visible:ring-2 focus-visible:ring-[#075E62] focus-visible:ring-offset-2 disabled:opacity-50",
  outline: "inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full font-medium text-sm transition-colors duration-150 bg-white text-[#075E62] border border-[#D5E7E5] hover:bg-[#D5E7E5]/30 focus-visible:ring-2 focus-visible:ring-[#35BBAE] focus-visible:ring-offset-2",
  ghost: "inline-flex items-center justify-center gap-2 h-9 px-3 rounded-full font-medium text-sm transition-colors duration-150 text-[#5F8588] hover:text-[#075E62] hover:bg-[#D5E7E5]/30",
  danger: "inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full font-medium text-sm transition-colors duration-150 bg-white text-[#A85751] border border-[#D5E7E5] hover:bg-[#A85751] hover:text-white focus-visible:ring-2 focus-visible:ring-[#A85751] focus-visible:ring-offset-2",
};

export const input =
  "w-full h-10 px-3.5 rounded-md border border-[#D5E7E5] bg-white text-sm text-[#075E62] placeholder:text-[#5F8588]/70 focus:border-[#35BBAE] focus:outline-none focus:ring-2 focus:ring-[#35BBAE]/30 transition-colors duration-150";
export const selectCls = input + " appearance-none";
export const label = "block text-xs font-bold uppercase tracking-wider text-[#5F8588] mb-1.5";
export const card = "bg-white border border-[#D5E7E5] rounded-lg";

export function Money({ amount, currency = "EUR", className = "", sign = false, type = null }) {
  const { localeTag } = useI18n();
  const n = Number(amount || 0);
  if (type === "income" || type === "expense") {
    const s = type === "expense" ? "−" : "+";
    return <span className={`tnum ${className}`}>{s}{fmtMoney(Math.abs(n), currency, localeTag)}</span>;
  }
  if (sign) return <span className={`tnum ${className}`}>{n < 0 ? "−" : "+"}{fmtMoney(Math.abs(n), currency, localeTag)}</span>;
  return <span className={`tnum ${className}`}>{fmtMoney(n, currency, localeTag)}</span>;
}

export function ScopeBadge({ scope }) {
  const { t } = useI18n();
  const map = {
    personal: { cls: "bg-[#D5E7E5]/40 text-[#075E62] border-[#D5E7E5]", key: "scope.personal", color: SCOPE_COLOR.personal },
    professional: { cls: "bg-[#35BBAE]/15 text-[#075E62] border-[#35BBAE]/50", key: "scope.professional", color: SCOPE_COLOR.professional },
    shared: { cls: "bg-[#05787E]/12 text-[#075E62] border-[#05787E]/40", key: "scope.shared", color: "#05787E" },
    unassigned: { cls: "bg-[#E3F0ED] text-[#5F8588] border-[#D5E7E5]", key: "scope.unassigned", color: SCOPE_COLOR.total },
  };
  const m = map[scope] || map.professional;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs border font-medium ${m.cls}`}>
      <span className="w-2 h-2 rounded-full" style={{ background: m.color }} /> {t(m.key)}
    </span>
  );
}

// Bloque Personal | Profesional | Total de la Visión general (Panel y Gráficos).
// Solo presentación; Total = Personal + Profesional, por moneda, sin convertir.
export function OverviewByScope({ personal, pro }) {
  const { t } = useI18n();
  const bcP = personal?.by_currency || {};
  const bcPro = pro?.by_currency || {};
  const curs = Array.from(new Set([...Object.keys(bcP), ...Object.keys(bcPro)]));
  if (!curs.length) return null;
  const rows = [["in", "income"], ["out", "outflow"], ["saved", "saved"], ["net", "result"]];
  const multi = curs.length > 1;
  return (
    <div className="ov-table" data-testid="overview-by-scope">
      <div className="ov-grid ov-head">
        <div className="ov-c0" />
        <div className="ov-col ov-h"><span className="ov-dot" style={{ background: SCOPE_COLOR.personal }} />{t("scope.personal")}</div>
        <div className="ov-col ov-h"><span className="ov-dot" style={{ background: SCOPE_COLOR.professional }} />{t("scope.professional")}</div>
        <div className="ov-col ov-h ov-tcol"><span className="ov-dot" style={{ background: SCOPE_COLOR.total }} />{t("ov.total")}</div>
      </div>
      {curs.map((c) => (
        <div className="ov-cur" key={c}>
          {multi && <div className="ov-cur-label" data-testid={`overview-cur-${c}`}>{c}</div>}
          {rows.map(([k, f]) => {
            const pv = Number(bcP[c]?.[f] || 0), prv = Number(bcPro[c]?.[f] || 0), tv = pv + prv;
            const isNet = k === "net";
            const nc = (v) => isNet ? (v < 0 ? "ov-neg" : "ov-pos") : "";
            const mv = (v) => fmtMoney(v, c).replace(/\u00A0/g, " ");
            return (
              <div className={`ov-grid ov-row${isNet ? " ov-netrow" : ""}`} key={k} data-testid={`overview-row-${c}-${k}`}>
                <div className="ov-c0">{t(`ov.${k}`)}</div>
                <div className={`ov-col ov-num ${nc(pv)}`} data-testid={`overview-personal-${c}-${k}`}>{mv(pv)}</div>
                <div className={`ov-col ov-num ${nc(prv)}`} data-testid={`overview-professional-${c}-${k}`}>{mv(prv)}</div>
                <div className={`ov-col ov-num ov-tcol ${nc(tv)}`} data-testid={`overview-total-${c}-${k}`}>{mv(tv)}</div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function PageTitle({ title, subtitle, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
      <div>
        <h1 className="text-3xl sm:text-4xl font-normal tracking-tight text-[#075E62] leading-[1.05]">{title}</h1>
        {subtitle && <p className="text-sm text-[#5F8588] mt-2 max-w-2xl">{subtitle}</p>}
      </div>
      {children && <div className="flex items-center gap-2 flex-wrap">{children}</div>}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-16 px-6">
      {Icon && (
        <div className="w-12 h-12 rounded-lg border border-[#D5E7E5] flex items-center justify-center mb-4 text-[#5F8588]">
          <Icon size={22} strokeWidth={1.75} />
        </div>
      )}
      <p className="text-base font-medium text-[#075E62]">{title}</p>
      {text && <p className="text-sm text-[#5F8588] mt-1 max-w-md">{text}</p>}
    </div>
  );
}

export function Spinner({ label }) {
  const { t } = useI18n();
  const text = label || t("common.loading");
  return (
    <div className="flex items-center justify-center py-20 text-[#5F8588] text-sm gap-3">
      <span className="w-4 h-4 border-2 border-[#D5E7E5] border-t-[#35BBAE] rounded-full animate-spin" />
      {text}
    </div>
  );
}
